import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'

import { MyHorizontalTabV2 } from '@interstellar-component'

import { PricingService } from '../wallet.service'
import { formatIDR } from '../billing.format'
import { golonganLabel } from '../wallet.format'

/**
 * "Calculate your enquiry credit cost" (Figma 15194:107308).
 *
 * Answers one question: at N enquiries a month, what does the credit itself cost
 * on each plan? Tarif comes from the plan the company would actually be billed
 * under, so the figure is the figure — no separate estimator maths lives here.
 *
 * THREE PLACES THIS DEPARTS FROM THE MOCKUP, each for a reason:
 *
 * 1. The Segment control switches the SIMULATION only. Golongan itself still moves
 *    solely through an application reviewed by SAW (BR-COMPANY-04), and buying a
 *    package from the other column is refused server-side. What it buys the user
 *    is the number behind the "Ajukan sebagai UMKM" button sitting at the top of
 *    this same page: 4-5x on tarif, ~14x on the subscription fee. Asking someone
 *    to decide that blind was the worse option. Switching away from the company's
 *    own golongan states plainly that it is a simulation.
 *
 * 2. The caption does NOT say "include applicable taxes". Under BR-PPN-03 PPN is
 *    collected once, when money ENTERS the system at top-up; spending saldo on an
 *    enquiry is never taxed again. So these numbers are net and that is correct —
 *    it is the mockup's caption that contradicts the rule.
 *
 * 3. Each card carries the plan's subscription cost underneath. Volume × tarif
 *    alone makes Business look cheapest at every volume, because its Rp6.000 rate
 *    is the lowest — while quietly costing Rp14.400.000 a year in platform fees.
 *    A comparison that inverts the answer is worse than no comparison.
 */
const MIN_VOLUME = 1
// Not in the BRD and not in the design — a placeholder wide enough to cover the
// plans' own volume bands (Starter <30, Growth 30–120, Business 120+). The number
// field takes anything; the slider is just the quick way to get there.
const MAX_VOLUME = 150

function PlanCalculator({ companyId, packages, golongan, period }) {
  const [volume, setVolume] = useState(12)
  const [segment, setSegment] = useState(golongan)
  const [other, setOther] = useState(null)

  useEffect(() => setSegment(golongan), [golongan])

  // The other column is fetched only when actually asked for — most viewers
  // never leave their own golongan.
  useEffect(() => {
    if (!companyId || !segment || segment === golongan) return
    let alive = true
    PricingService.listPackages(companyId, { golongan: segment, limit: 50 })
      .then((res) => {
        const body = res?.data ?? res
        if (alive) setOther(body?.data ?? body ?? [])
      })
      .catch(() => alive && setOther([]))
    return () => {
      alive = false
    }
  }, [companyId, segment, golongan])

  const source = segment === golongan ? packages : other || []
  const simulating = segment !== golongan

  const rows = useMemo(
    () =>
      // One card per TIER, semuanya di periode yang SAMA — menyandingkan baris
      // 30 hari dan 365 hari membandingkan dua hal berbeda. Periodenya ikut tab
      // Monthly/Annual di atas; dikunci 30 hari, seksi ini pernah menulis
      // "+ langganan Rp 1.500.000 / 30 hari" di bawah deretan kartu tahunan.
      source
        .filter((p) => p.is_payg || Number(p.period_days) === period)
        .map((pkg) => ({
          id: pkg.id,
          name: pkg.name,
          enquiryCost: volume * Number(pkg.tarif_enquiry_idr || 0),
          platformNote: pkg.is_payg
            ? 'No subscription fee'
            : `+ subscription ${formatIDR(pkg.harga_periode_idr)} / ${pkg.period_label}`,
        })),
    [source, volume, period],
  )

  if (!packages.length) return null

  const pct = ((volume - MIN_VOLUME) / (MAX_VOLUME - MIN_VOLUME)) * 100

  return (
    <section className="border-t border-gray-light/200 pt-10">
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,380px)_1fr]">
        <div>
          <p className="text-sm font-semibold text-brand/700">Calculator</p>
          <h2 className="mt-2 text-3xl font-semibold tracking-tight text-gray-light/900">
            Calculate your enquiry credit cost
          </h2>
          <p className="mt-3 text-sm text-gray-light/600">
            The credit enquiry cost is calculated from the plan rate that applies to your
            company. This figure is net — PPN is charged once when you top up saldo, and
            spending it is not taxed again.
          </p>
        </div>

        <div className="rounded-2xl border border-gray-light/200 bg-white p-5 shadow-xs">
          <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
            <div className="sm:w-[200px] sm:shrink-0">
              <p className="text-sm font-medium text-gray-light/900">Segment</p>
              {/* Same design-system tab as the Monthly/Annual control above it —
                  two segmented controls on one page should not be two different
                  components. */}
              <div className="mt-2">
                <MyHorizontalTabV2
                  value={segment}
                  onChange={setSegment}
                  tabs={['umkm', 'korporat'].map((g) => ({ value: g, label: golonganLabel(g) }))}
                  fitContent
                />
              </div>
              {simulating ? (
                <p className="mt-2 text-xs text-warning/700">
                  Simulating the {golonganLabel(segment)} segment. Your company is still{' '}
                  {golonganLabel(golongan)} — you are billed at {golonganLabel(golongan)} rates,
                  and they are not adjusted if an application is approved later.
                </p>
              ) : (
                <p className="mt-2 text-xs text-gray-light/500">
                  Your company's segment. The plan cards above follow it.
                </p>
              )}
            </div>

            <div className="flex-1 sm:border-l sm:border-gray-light/200 sm:pl-6">
              <div className="flex items-end justify-between gap-4">
                <label
                  htmlFor="calc-volume"
                  className="text-sm font-medium text-gray-light/900"
                >
                  Enquiry volume
                </label>
                <input
                  id="calc-volume-number"
                  inputMode="numeric"
                  value={volume}
                  onChange={(e) => {
                    const n = parseInt(String(e.target.value).replace(/\D/g, ''), 10)
                    setVolume(
                      Number.isFinite(n)
                        ? Math.min(MAX_VOLUME, Math.max(MIN_VOLUME, n))
                        : MIN_VOLUME,
                    )
                  }}
                  className="w-16 border-b-2 border-gray-light/300 bg-transparent pb-0.5 text-right text-2xl font-semibold text-gray-light/900 outline-none focus:border-brand/600"
                  aria-label="Enquiry volume"
                />
              </div>

              <input
                id="calc-volume"
                type="range"
                min={MIN_VOLUME}
                max={MAX_VOLUME}
                value={volume}
                onChange={(e) => setVolume(Number(e.target.value))}
                className="mt-4 h-2 w-full cursor-pointer appearance-none rounded-full outline-none [&::-moz-range-thumb]:size-5 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-brand/600 [&::-moz-range-thumb]:bg-white [&::-webkit-slider-thumb]:size-5 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-brand/600 [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:shadow-sm"
                style={{
                  background: `linear-gradient(to right, rgb(30 58 138) ${pct}%, rgb(228 231 235) ${pct}%)`,
                }}
              />
              <div className="mt-1.5 flex justify-between text-xs text-gray-light/500">
                <span>{MIN_VOLUME}</span>
                <span>{MAX_VOLUME}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {rows.map((r) => (
          <div
            key={r.id}
            className="rounded-xl border border-gray-light/200 bg-white p-4 shadow-xs"
          >
            <p className="text-sm text-gray-light/600">{r.name}</p>
            <p className="mt-1 text-2xl font-semibold tracking-tight text-gray-light/900">
              {formatIDR(r.enquiryCost)}
            </p>
            <p className="mt-1 text-xs text-gray-light/500">{r.platformNote}</p>
          </div>
        ))}
      </div>
    </section>
  )
}

PlanCalculator.propTypes = {
  companyId: PropTypes.string,
  packages: PropTypes.array,
  golongan: PropTypes.string,
  period: PropTypes.number,
}

PlanCalculator.defaultProps = {
  companyId: null,
  packages: [],
  golongan: null,
  period: 30,
}

export default PlanCalculator
